import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { cn } from '../utils/cn';
import { CONTACT_INFO, SOCIAL_LINKS } from '../constants/contact';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Rooms', href: '/rooms' },
  { name: 'Services', href: '/services' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'News', href: '/news' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const location = useLocation();  // Scroll detection to show/hide contact bar
  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>      {/* Contact Details Bar - Only visible when at top */}
      <div 
        className={`bg-neutral-100 border-b border-neutral-200 transition-all duration-700 ease-in-out transform origin-top ${
          isAtTop 
            ? 'max-h-20 opacity-100 scale-y-100 translate-y-0' 
            : 'max-h-0 opacity-0 scale-y-0 -translate-y-2 overflow-hidden'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className={`flex items-center justify-between py-2 text-sm transition-all duration-500 ease-out transform ${
            isAtTop ? 'opacity-100 translate-y-0 animate-slideInFromTop' : 'opacity-0 -translate-y-4'
          }`}>            {/* Left - Contact Info */}
            <div className="flex items-center space-x-6">              <a 
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center space-x-2 text-neutral-600 cursor-pointer hover:text-primary-600 transition-colors group relative focus:outline-none active:outline-none"
                title="Call phone number"
              >
                <PhoneIcon className="h-4 w-4 text-neutral-500 group-hover:text-primary-500" />
                <span className="select-none">{CONTACT_INFO.phone}</span>
              </a>
              <a 
                href={SOCIAL_LINKS.whatsapp}
                className="flex items-center space-x-2 text-neutral-600 cursor-pointer hover:text-primary-600 transition-colors group relative"
                title="Message on WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-4 w-4 text-neutral-500 group-hover:text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                </svg>
                <span className="select-none hidden sm:inline">WhatsApp</span>
              </a>
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="hidden sm:flex items-center space-x-2 text-neutral-600 cursor-pointer hover:text-primary-600 transition-colors group relative"
                title="Send email"
              >
                <EnvelopeIcon className="h-4 w-4 text-neutral-500 group-hover:text-primary-500" />
                <span className="select-none">{CONTACT_INFO.email}</span>
              </a>
            </div>{/* Right - Social Media Icons */}
            <div className="flex items-center space-x-3">
              <a href={SOCIAL_LINKS.facebook} className="text-neutral-500 hover:text-primary-600 transition-colors" target="_blank" rel="noopener noreferrer">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href={`https://maps.google.com?q=${CONTACT_INFO.coordinates.lat},${CONTACT_INFO.coordinates.lng}`} className="text-neutral-500 hover:text-primary-600 transition-colors" target="_blank" rel="noopener noreferrer">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>      {/* Main Navbar */}      <header className="bg-white/95 backdrop-blur-sm shadow-soft sticky top-0 z-50 relative">
      <nav className="mx-auto flex max-w-7xl items-center justify-between p-3 lg:px-8" aria-label="Global"><div className="flex lg:flex-1">
          <Link to="/" className="">            <img 
              src="/images/bethanchok-logo.png" 
              alt="Bethanchok Homestay" 
              className="h-12 lg:h-16 w-auto"
            />
          </Link>
        </div><div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-neutral-700 transition-all duration-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="sr-only">{mobileMenuOpen ? 'Close main menu' : 'Open main menu'}</span>            <div className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-8 w-8" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-8 w-8" aria-hidden="true" />
              )}
            </div>
          </button>
        </div><div className="hidden lg:flex lg:gap-x-12">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "text-base font-semibold leading-6 transition-colors duration-200",
                location.pathname === item.href
                  ? "text-primary-600"
                  : "text-neutral-900 hover:text-primary-600"
              )}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </nav>      {/* Mobile menu - simple vertical dropdown with sweet animation */}
      <div 
        className={`lg:hidden absolute top-full left-0 right-0 bg-white mobile-dropdown-shadow z-40 transition-all duration-300 ease-out transform ${
          mobileMenuOpen 
            ? 'opacity-100 translate-y-0 scale-y-100' 
            : 'opacity-0 -translate-y-2 scale-y-95 pointer-events-none'
        }`}
        style={{ transformOrigin: 'top' }}
      >
        <div className="px-6 py-4 space-y-1">
          {navigation.map((item, index) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "mobile-menu-item block px-4 py-3 text-base font-medium rounded-lg transition-all duration-200",
                location.pathname === item.href
                  ? "bg-primary-600 text-white shadow-lg"
                  : "text-neutral-700 hover:bg-primary-50 hover:text-primary-600"
              )}
              style={{
                animationDelay: mobileMenuOpen ? `${index * 50}ms` : '0ms',
                animation: mobileMenuOpen ? 'slideInFromTop 0.4s ease-out' : 'none'
              }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}        </div>
      </div>
    </header>
    </>
  );
}
